import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Mail, MapPin, Wallet, KeyRound, ShieldCheck, CheckCircle2, Save, CreditCard, Image as ImageIcon, DatabaseZap, Calendar } from 'lucide-react';
import Swal from 'sweetalert2';

const CustomerDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  
  // Editable States
  const [walletBalance, setWalletBalance] = useState('');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await fetch(`https://motilalalgo.com/api/admin/customer/${id}`);
      const data = await response.json();
      if (data.success) {
        setUser(data.user);
        setWalletBalance(data.user.wallet_balance || '0');
        setLoginId(data.user.login_id || '');
        setPassword(data.user.raw_password || '');
      }
    } catch (error) {
      console.error("Fetch Customer Error:", error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`https://motilalalgo.com/api/admin/customer/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_balance: parseFloat(walletBalance),
          login_id: loginId,
          password: password
        })
      });
      
      const data = await response.json();
      if (data.success) {
        Swal.fire({ title: 'Updated!', text: data.message, icon: 'success', confirmButtonColor: '#3b36a4' });
        fetchCustomerDetails(); // Refresh data
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Server connection failed', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return <div className="p-8 text-center text-slate-500 font-bold">Loading Customer Details...</div>;

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 pb-10">
      
      {/* Header */}
      <div className="flex items-center gap-4 bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-slate-100">
        <button onClick={() => navigate(-1)} className="p-2.5 bg-slate-50 text-slate-500 hover:bg-[#3b36a4] hover:text-white rounded-xl transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-black text-slate-800 flex items-center gap-3">
            {user.first_name} {user.last_name}
            {user.login_id ? (
              <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md font-bold flex items-center gap-1"><CheckCircle2 size={12}/> Active</span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 px-2 py-1 rounded-md font-bold">Pending</span>
            )}
          </h1>
          <p className="text-sm text-slate-400 font-medium mt-1">Customer ID: {user.id} • Joined: {new Date(user.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Personal Info */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
              <User className="text-[#3b36a4]" size={20} />
              <h2 className="text-lg font-black text-slate-800">Personal Details</h2>
            </div>
            
            <div className="space-y-4">
              <DetailRow icon={<Mail size={16} />} label="Email Address" value={user.email} />
              <DetailRow icon={<Phone size={16} />} label="Mobile Number" value={user.mobile_number} />
              <DetailRow icon={<Calendar size={16} />} label="Date of Birth" value={user.dob} />
              <DetailRow icon={<ShieldCheck size={16} />} label="Aadhar No" value={user.aadhar_no} />
              <DetailRow icon={<CreditCard size={16} />} label="PAN Number" value={user.pan_no} />
              <DetailRow icon={<MapPin size={16} />} label="Address" value={`${user.city} (Full details on record)`} />
              
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Documents</p>
                <a href={`https://motilalalgo.com/${user.photo_doc?.replace(/\\/g, '/')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-bold text-[#3b36a4] bg-indigo-50 px-3 py-2 rounded-xl hover:bg-indigo-100 transition-colors w-fit">
                  <ImageIcon size={16} /> View Passport Photo
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Wallet & Credentials Management */}
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleUpdate} className="space-y-6">
            
            {/* Wallet Balance Card */}
            <div className="bg-gradient-to-br from-[#2d2780] to-[#3b36a4] rounded-2xl shadow-lg p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-indigo-200 mb-2">
                    <Wallet size={20} />
                    <h2 className="text-sm font-bold uppercase tracking-widest">System Wallet Balance</h2>
                  </div>
                  <p className="text-xs text-indigo-300">Update the user's trading capital here.</p>
                </div>
                <div className="relative w-full sm:w-64">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-800 font-black text-xl">₹</span>
                  <input 
                    type="number" 
                    step="0.01"
                    value={walletBalance}
                    onChange={(e) => setWalletBalance(e.target.value)}
                    className="w-full pl-10 pr-4 py-4 bg-white rounded-xl text-slate-800 font-black text-2xl outline-none focus:ring-4 focus:ring-indigo-500/50 shadow-inner"
                  />
                </div>
              </div>
            </div>

            {/* Credentials Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <KeyRound className="text-amber-500" size={20} />
                <h2 className="text-lg font-black text-slate-800">Account Credentials</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Login ID</label>
                  <input 
                    type="text" 
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    placeholder="Enter unique Login ID"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-[#3b36a4]/20 focus:border-[#3b36a4] outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Password</label>
                  <input 
                    type="text" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Set new password"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-[#3b36a4]/20 focus:border-[#3b36a4] outline-none transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button 
                type="submit" 
                disabled={isSaving}
                className="flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white font-black rounded-xl shadow-lg shadow-emerald-500/30 hover:bg-emerald-600 transition-all active:scale-95 disabled:opacity-70"
              >
                <Save size={20} />
                {isSaving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
};

// Helper Component
const DetailRow = ({ icon, label, value }: any) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 text-slate-400">{icon}</div>
    <div>
      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="text-sm font-bold text-slate-700">{value || 'N/A'}</p>
    </div>
  </div>
);

export default CustomerDetails;